import React, { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Share2, Twitter, Instagram, Download, Check, Link2, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';

// Beautiful shareable card with watermark
export default function WholePictureCard({ 
  type, // 'story' | 'person' | 'connection'
  data,
  trigger
}) {
  const cardRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateImage = async () => {
    if (!cardRef.current) return null;
    
    setIsGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#050505'
      });
      return dataUrl;
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = async () => {
    const dataUrl = await generateImage();
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.download = `res-tota-ipsum-${type}-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
    toast.success('Image downloaded!');
  };

  const shareToTwitter = async () => {
    const shareText = getShareText();
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`${shareText} #ResTotaIpsum #UFO #Disclosure`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareToInstagram = async () => {
    // Instagram doesn't support direct sharing, so we download the image
    const dataUrl = await generateImage();
    if (!dataUrl) return;

    // Download image for Instagram
    const link = document.createElement('a');
    link.download = `res-tota-ipsum-${type}.png`;
    link.href = dataUrl;
    link.click();
    
    toast.success('Image downloaded! Open Instagram and share from your gallery.');
  };

  const shareToTikTok = async () => {
    // TikTok doesn't have a web share API, download image
    const dataUrl = await generateImage();
    if (!dataUrl) return;

    const link = document.createElement('a');
    link.download = `res-tota-ipsum-${type}.png`;
    link.href = dataUrl;
    link.click();
    
    toast.success('Image downloaded! Open TikTok and share from your gallery.');
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getShareText = () => {
    if (type === 'story') {
      return `🌌 ${data.title} - Discover the whole picture`;
    } else if (type === 'person') {
      return `👤 ${data.name} - ${data.role} | Explore the connections`;
    } else if (type === 'connection') {
      return `🔗 ${data.source} ↔ ${data.target} | See how everything connects`;
    }
    return 'Discover the whole picture at Res Tota Ipsum';
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" className="flex items-center gap-2 text-white/60 hover:text-white" data-testid="share-card-trigger">
            <Share2 className="w-5 h-5" />
            Share
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg bg-[#0A0A0A] border-white/10">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Share the Whole Picture</DialogTitle>
        </DialogHeader>

        {/* Preview Card */}
        <div className="my-4">
          <div 
            ref={cardRef}
            className="relative overflow-hidden rounded-sm"
            style={{ backgroundColor: '#050505' }}
          >
            {/* Card Content */}
            <div className="p-6" style={{ background: 'linear-gradient(135deg, #050505 0%, #0A0A0A 50%, #050505 100%)' }}>
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                    <span className="text-black font-bold text-xs">RTI</span>
                  </div>
                  <span className="font-heading text-sm tracking-widest text-white/80">RES TOTA IPSUM</span>
                </div>
                <span className="text-cyan-400 text-xs font-mono uppercase tracking-wider">The Whole Picture</span>
              </div>

              {/* Type-specific Content */}
              {type === 'story' && (
                <div>
                  {data.image && (
                    <div className="h-40 mb-4 rounded-sm overflow-hidden">
                      <img src={data.image} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <span className="inline-block px-2 py-1 text-xs font-mono uppercase tracking-wider bg-cyan-400/20 text-cyan-400 rounded-sm mb-3">
                    {data.category}
                  </span>
                  <h3 className="font-heading text-2xl font-bold text-white mb-2 leading-tight">{data.title}</h3>
                  <p className="text-white/60 text-sm line-clamp-3">{data.excerpt}</p>
                </div>
              )}

              {type === 'person' && (
                <div className="flex items-start gap-4">
                  {data.image && (
                    <img src={data.image} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-cyan-400/30" />
                  )}
                  <div className="flex-1">
                    <h3 className="font-heading text-2xl font-bold text-white mb-1">{data.name}</h3>
                    <p className="text-cyan-400 font-mono text-sm mb-3">{data.role}</p>
                    <p className="text-white/60 text-sm line-clamp-3">{data.bio}</p>
                  </div>
                </div>
              )}

              {type === 'connection' && (
                <div className="text-center py-6">
                  <div className="flex items-center justify-center gap-6">
                    <div className="text-right">
                      <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-cyan-400/20 flex items-center justify-center">
                        <span className="text-cyan-400 font-bold">{data.source?.charAt(0)}</span>
                      </div>
                      <p className="font-heading font-bold text-white">{data.source}</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-20 h-[2px] bg-gradient-to-r from-cyan-400 to-yellow-400" />
                      <span className="text-xs font-mono uppercase text-white/50 mt-2">{data.relationship}</span>
                    </div>
                    <div className="text-left">
                      <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-yellow-400/20 flex items-center justify-center">
                        <span className="text-yellow-400 font-bold">{data.target?.charAt(0)}</span>
                      </div>
                      <p className="font-heading font-bold text-white">{data.target}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Watermark */}
              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center">
                    <span className="text-black font-bold text-[8px]">RTI</span>
                  </div>
                  <span className="text-white/40 text-xs">restotaipsum.com</span>
                </div>
                <span className="text-white/30 text-xs font-mono">THE WHOLE OF IT LAID OUT BEFORE YOU</span>
              </div>
            </div>

            {/* Geometric pattern overlay */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-5"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2300F0FF' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}
            />
          </div>
        </div>

        {/* Share Buttons */}
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={shareToTwitter}
            className="flex items-center justify-center gap-2 bg-[#1DA1F2] hover:bg-[#1a8cd8] text-white"
            data-testid="share-twitter"
          >
            <Twitter className="w-4 h-4" />
            Share on X
          </Button>
          
          <Button
            onClick={shareToInstagram}
            disabled={isGenerating}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white"
            data-testid="share-instagram"
          >
            <Instagram className="w-4 h-4" />
            Instagram
          </Button>
          
          <Button
            onClick={shareToTikTok}
            disabled={isGenerating}
            className="flex items-center justify-center gap-2 bg-black border border-white/20 hover:bg-white/10 text-white"
            data-testid="share-tiktok"
          >
            <ExternalLink className="w-4 h-4" />
            TikTok
          </Button>
          
          <Button
            onClick={downloadImage}
            disabled={isGenerating}
            className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white"
            data-testid="download-image"
          >
            <Download className="w-4 h-4" />
            {isGenerating ? 'Generating...' : 'Download'}
          </Button>
        </div>

        {/* Copy Link */}
        <Button
          onClick={copyLink}
          variant="ghost"
          className="w-full mt-2 text-white/60 hover:text-white"
          data-testid="copy-link"
        >
          {copied ? <Check className="w-4 h-4 mr-2" /> : <Link2 className="w-4 h-4 mr-2" />}
          {copied ? 'Link Copied!' : 'Copy Link'}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
